/**
 * Turizm karbon hesaplamaları için localStorage yönetim modülü
 */

const STORAGE_KEYS = {
  CALCULATIONS: 'tourism_carbon_calculations',
  SETTINGS: 'tourism_carbon_settings',
  COMPARISONS: 'tourism_carbon_comparisons'
};

class TourismStorageManager {
  /**
   * Yeni hesaplama kaydını saklar
   * @param {Object} calculation - Hesaplama verisi
   * @returns {string} Kaydın ID'si
   */
  static saveCalculation(calculation) {
    try {
      const calculations = this.getAllCalculations();
      const id = this.generateId();
      
      const calculationRecord = {
        id,
        ...calculation,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      calculations.push(calculationRecord);
      localStorage.setItem(STORAGE_KEYS.CALCULATIONS, JSON.stringify(calculations));
      
      return id;
    } catch (error) {
      console.error('Hesaplama kaydedilemedi:', error);
      throw new Error('Hesaplama kaydedilemedi');
    }
  }

  /**
   * Tüm hesaplamaları getirir
   * @returns {Array} Hesaplamalar listesi
   */
  static getAllCalculations() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CALCULATIONS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Hesaplamalar yüklenemedi:', error);
      return [];
    }
  }

  /**
   * ID'ye göre hesaplama getirir
   * @param {string} id - Hesaplama ID'si
   * @returns {Object|null} Hesaplama verisi
   */
  static getCalculationById(id) {
    try {
      const calculations = this.getAllCalculations();
      return calculations.find(calc => calc.id === id) || null;
    } catch (error) {
      console.error('Hesaplama bulunamadı:', error);
      return null;
    }
  }

  /**
   * Hesaplamayı günceller
   * @param {string} id - Hesaplama ID'si
   * @param {Object} updates - Güncellenecek veriler
   * @returns {boolean} Başarı durumu
   */
  static updateCalculation(id, updates) {
    try {
      const calculations = this.getAllCalculations();
      const index = calculations.findIndex(calc => calc.id === id);
      
      if (index === -1) {
        return false;
      }

      calculations[index] = {
        ...calculations[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(STORAGE_KEYS.CALCULATIONS, JSON.stringify(calculations));
      return true;
    } catch (error) {
      console.error('Hesaplama güncellenemedi:', error);
      return false;
    }
  }

  /**
   * Hesaplamayı siler
   * @param {string} id - Hesaplama ID'si
   * @returns {boolean} Başarı durumu
   */
  static deleteCalculation(id) {
    try {
      const calculations = this.getAllCalculations();
      const filtered = calculations.filter(calc => calc.id !== id);
      
      localStorage.setItem(STORAGE_KEYS.CALCULATIONS, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Hesaplama silinemedi:', error);
      return false;
    }
  }

  /**
   * Tarih aralığına göre hesaplamaları filtreler
   * @param {string} startDate - Başlangıç tarihi (ISO string)
   * @param {string} endDate - Bitiş tarihi (ISO string)
   * @returns {Array} Filtrelenmiş hesaplamalar
   */
  static getCalculationsByDateRange(startDate, endDate) {
    try {
      const calculations = this.getAllCalculations();
      return calculations.filter(calc => {
        const calcDate = new Date(calc.createdAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        return calcDate >= start && calcDate <= end;
      });
    } catch (error) {
      console.error('Tarih filtresi uygulanamadı:', error);
      return [];
    }
  }

  /**
   * Periyoda göre hesaplamaları filtreler
   * @param {string} period - daily, monthly, yearly
   * @returns {Array} Filtrelenmiş hesaplamalar
   */
  static getCalculationsByPeriod(period) {
    try {
      const calculations = this.getAllCalculations();
      return calculations.filter(calc => calc.period === period);
    } catch (error) {
      console.error('Periyot filtresi uygulanamadı:', error);
      return [];
    }
  }

  /**
   * Son N hesaplamayı getirir
   * @param {number} count - Getirilecek hesaplama sayısı
   * @returns {Array} Son hesaplamalar
   */
  static getRecentCalculations(count = 10) {
    try {
      const calculations = this.getAllCalculations();
      return calculations
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, count);
    } catch (error) {
      console.error('Son hesaplamalar alınamadı:', error);
      return [];
    }
  }

  /**
   * Hesaplamalara not ekler
   * @param {string} id - Hesaplama ID'si
   * @param {string} note - Not metni
   * @returns {boolean} Başarı durumu
   */
  static addNote(id, note) {
    try {
      const calculation = this.getCalculationById(id);
      if (!calculation) return false;

      const notes = calculation.notes || [];
      notes.push({
        id: this.generateId(),
        text: note,
        createdAt: new Date().toISOString()
      });

      return this.updateCalculation(id, { notes });
    } catch (error) {
      console.error('Not eklenemedi:', error);
      return false;
    }
  }

  /**
   * Karşılaştırma grubunu saklar
   * @param {Object} comparison - Karşılaştırma verisi
   * @returns {string} Karşılaştırma ID'si
   */
  static saveComparison(comparison) {
    try {
      const comparisons = this.getAllComparisons();
      const id = this.generateId();
      
      const comparisonRecord = {
        id,
        ...comparison,
        createdAt: new Date().toISOString()
      };

      comparisons.push(comparisonRecord);
      localStorage.setItem(STORAGE_KEYS.COMPARISONS, JSON.stringify(comparisons));
      
      return id;
    } catch (error) {
      console.error('Karşılaştırma kaydedilemedi:', error);
      throw new Error('Karşılaştırma kaydedilemedi');
    }
  }

  /**
   * Tüm karşılaştırmaları getirir
   * @returns {Array} Karşılaştırmalar listesi
   */
  static getAllComparisons() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.COMPARISONS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Karşılaştırmalar yüklenemedi:', error);
      return [];
    }
  }

  /**
   * Uygulama ayarlarını saklar
   * @param {Object} settings - Ayarlar
   * @returns {boolean} Başarı durumu
   */
  static saveSettings(settings) {
    try {
      const currentSettings = this.getSettings();
      const updatedSettings = {
        ...currentSettings,
        ...settings,
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(updatedSettings));
      return true;
    } catch (error) {
      console.error('Ayarlar kaydedilemedi:', error);
      return false;
    }
  }

  /**
   * Uygulama ayarlarını getirir
   * @returns {Object} Ayarlar
   */
  static getSettings() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      const defaultSettings = {
        currency: 'TRY',
        language: 'tr',
        theme: 'light',
        defaultPeriod: 'monthly',
        autoSave: true,
        notifications: true,
        certificationTarget: 'greenKey'
      };

      return stored ? { ...defaultSettings, ...JSON.parse(stored) } : defaultSettings;
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
      return {};
    }
  }

  /**
   * İstatistikleri hesaplar
   * @returns {Object} İstatistik verileri
   */
  static getStatistics() {
    try {
      const calculations = this.getAllCalculations();
      
      if (calculations.length === 0) {
        return {
          totalCalculations: 0,
          averageEmission: 0,
          totalEmission: 0,
          lastCalculation: null,
          periodDistribution: {},
          monthlyTrend: []
        };
      }

      const totalEmission = calculations.reduce((sum, calc) => sum + calc.total, 0);
      const averageEmission = totalEmission / calculations.length;
      
      // Periyot dağılımı
      const periodDistribution = calculations.reduce((acc, calc) => {
        acc[calc.period] = (acc[calc.period] || 0) + 1;
        return acc;
      }, {});

      // Aylık trend
      const monthlyData = {};
      calculations.forEach(calc => {
        const month = new Date(calc.createdAt).toISOString().substring(0, 7);
        if (!monthlyData[month]) {
          monthlyData[month] = { count: 0, total: 0 };
        }
        monthlyData[month].count++;
        monthlyData[month].total += calc.total;
      });

      const monthlyTrend = Object.entries(monthlyData)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, data]) => ({
          month,
          count: data.count,
          average: data.total / data.count
        }));

      return {
        totalCalculations: calculations.length,
        averageEmission: Math.round(averageEmission * 100) / 100,
        totalEmission: Math.round(totalEmission * 100) / 100,
        lastCalculation: calculations[calculations.length - 1],
        periodDistribution,
        monthlyTrend
      };
    } catch (error) {
      console.error('İstatistikler hesaplanamadı:', error);
      return {};
    }
  }

  /**
   * Verileri dışa aktarma için hazırlar
   * @param {Array} calculationIds - Dışa aktarılacak hesaplama ID'leri
   * @returns {Object} Export verisi
   */
  static exportData(calculationIds = []) {
    try {
      const allCalculations = this.getAllCalculations();
      const calculations = calculationIds.length > 0 
        ? allCalculations.filter(calc => calculationIds.includes(calc.id))
        : allCalculations;

      return {
        exportDate: new Date().toISOString(),
        version: '1.0',
        calculations,
        settings: this.getSettings(),
        statistics: this.getStatistics()
      };
    } catch (error) {
      console.error('Veri dışa aktarılamadı:', error);
      throw new Error('Veri dışa aktarılamadı');
    }
  }

  /**
   * Verileri içe aktarır
   * @param {Object} data - İçe aktarılacak veri
   * @param {boolean} merge - Mevcut verilerle birleştir
   * @returns {boolean} Başarı durumu
   */
  static importData(data, merge = true) {
    try {
      if (!data.calculations) {
        throw new Error('Geçersiz veri formatı');
      }

      if (merge) {
        const existingCalculations = this.getAllCalculations();
        const newCalculations = data.calculations.filter(calc => 
          !existingCalculations.some(existing => existing.id === calc.id)
        );
        const mergedCalculations = [...existingCalculations, ...newCalculations];
        localStorage.setItem(STORAGE_KEYS.CALCULATIONS, JSON.stringify(mergedCalculations));
      } else {
        localStorage.setItem(STORAGE_KEYS.CALCULATIONS, JSON.stringify(data.calculations));
      }

      if (data.settings) {
        this.saveSettings(data.settings);
      }

      return true;
    } catch (error) {
      console.error('Veri içe aktarılamadı:', error);
      return false;
    }
  }

  /**
   * Tüm verileri temizler
   * @returns {boolean} Başarı durumu
   */
  static clearAllData() {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Veriler temizlenemedi:', error);
      return false;
    }
  }

  /**
   * Benzersiz ID üretir
   * @returns {string} Benzersiz ID
   */
  static generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Storage boyutunu kontrol eder
   * @returns {Object} Boyut bilgileri
   */
  static getStorageInfo() {
    try {
      let totalSize = 0;
      const sizes = {};

      Object.entries(STORAGE_KEYS).forEach(([key, storageKey]) => {
        const data = localStorage.getItem(storageKey);
        const size = data ? new Blob([data]).size : 0;
        sizes[key] = size;
        totalSize += size;
      });

      return {
        totalSize,
        sizes,
        available: 5 * 1024 * 1024 - totalSize, // 5MB limit assumption
        percentage: (totalSize / (5 * 1024 * 1024)) * 100
      };
    } catch (error) {
      console.error('Storage bilgisi alınamadı:', error);
      return { totalSize: 0, sizes: {}, available: 0, percentage: 0 };
    }
  }
}

export default TourismStorageManager;
